class WelcomePopup extends HTMLElement {
  connectedCallback() {
    this.dialogComponent = this.querySelector('dialog-component');
    if (!this.dialogComponent) return;

    this.storageKey = this.dataset.storageKey || 'welcome-popup-subscribed';
    this.lastShownKey = this.storageKey + '-last-shown';
    this.delay = Number(this.dataset.delay) || 0;
    // Present only when the merchant enables "Limit how often it reappears" in the
    // theme editor — undefined/NaN here means that feature is off, so shouldShow()
    // falls back to the original every-visit behavior.
    this.reappearDays = Number(this.dataset.reappearDays) || null;

    // Only a confirmed, server-validated subscription stops future popups —
    // closing, dismissing, or an invalid email attempt should not.
    if (this.dataset.subscribed === 'true') {
      this.remember();
    }

    // Was placed after the early-return below, which fires in exactly the success
    // case this exists for — the copy-to-clipboard handler never attached when the
    // discount code was actually showing. Moved before both branches so it always runs.
    this.setupCodeCopy();
    this.setupValidation();

    if (this.dataset.forceOpen === 'true') {
      // Page just reloaded after a form submission (success or validation error) —
      // show it immediately so the visitor actually sees that message, ignoring
      // both the normal delay and the "already subscribed"/reappear-cooldown checks.
      this.dialogComponent.showDialog?.();
      return;
    }

    if (this.shouldShow()) {
      this.timeoutId = window.setTimeout(() => {
        this.dialogComponent.showDialog?.();
        this.rememberShown();
      }, this.delay);
    }
  }

  // Click-to-copy discount code shown on successful signup (matches the reference
  // UX the client pointed to: click the code, it copies, the box briefly shows
  // "Copied!" before reverting to the code text).
  setupCodeCopy() {
    var button = this.querySelector('[data-copy-code]');
    if (!button) return;

    var textEl = button.querySelector('[data-copy-code-text]');
    var code = button.dataset.copyCode;
    var originalText = textEl ? textEl.textContent : code;

    button.addEventListener('click', function () {
      var showCopied = function () {
        if (textEl) textEl.textContent = 'Copied!';
        window.setTimeout(function () {
          if (textEl) textEl.textContent = originalText;
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(showCopied, showCopied);
      } else {
        showCopied();
      }
    });
  }

  // History: this used to intercept the submit (preventDefault + fetch) to drive
  // the success/error UI directly, since form.posted_successfully? had seemed
  // unreliable. Root cause found 2026-09-30 via the actual response body of that
  // fetch: "Missing CAPTCHA token" — Shopify's CAPTCHA protection on this form
  // endpoint only attaches its token during a genuine, native, browser-driven
  // submission; intercepting the submit event and sending it via fetch() skips
  // whatever Shopify's own script does to attach that token first, so EVERY
  // submission through that path was rejected, silently (the old code showed
  // "success" regardless of the response — see git history / prior comments).
  // That's almost certainly also why form.posted_successfully? looked unreliable
  // before: the submission was never actually succeeding in the first place.
  //
  // Fix: don't intercept a *valid* submission at all — only block an invalid one
  // (bad email), which is a normal, legitimate use of preventDefault that never
  // reaches the server either way. A valid submission now goes out exactly like
  // the working footer newsletter signup does: a real, un-intercepted native POST,
  // full page reload, letting Shopify's own CAPTCHA flow run as intended. The
  // resulting success/error state is driven by form.posted_successfully? /
  // form.errors (Liquid, rendered server-side elsewhere in this section) plus
  // data-force-open above, which reopens the dialog on reload so the visitor
  // actually sees the result.
  setupValidation() {
    var form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', function (event) {
      var emailInput = form.querySelector('input[type="email"]');
      if (emailInput && !emailInput.checkValidity()) {
        event.preventDefault();
        emailInput.reportValidity();
      }
      // Valid input: no preventDefault — let it submit natively.
    });
  }

  shouldShow() {
    try {
      if (window.localStorage.getItem(this.storageKey) === 'true') return false;

      if (this.reappearDays) {
        var lastShown = Number(window.localStorage.getItem(this.lastShownKey));
        if (lastShown) {
          var elapsedMs = Date.now() - lastShown;
          var cooldownMs = this.reappearDays * 24 * 60 * 60 * 1000;
          if (elapsedMs < cooldownMs) return false;
        }
      }

      return true;
    } catch (error) {
      return true;
    }
  }

  remember() {
    try {
      window.localStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      // Storage unavailable (private browsing, etc.) — fail silently, popup will just show again next visit.
    }
  }

  rememberShown() {
    if (!this.reappearDays) return;
    try {
      window.localStorage.setItem(this.lastShownKey, String(Date.now()));
    } catch (error) {
      // Storage unavailable — fail silently, the day-based cooldown just won't apply.
    }
  }

  disconnectedCallback() {
    if (this.timeoutId) window.clearTimeout(this.timeoutId);
  }
}

if (!customElements.get('welcome-popup')) {
  customElements.define('welcome-popup', WelcomePopup);
}
